import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageWrapper from '../../../components/pageComponents/PageWrapper';
import ModalEnabled from '../../../components/modalComponents/ModalEnabled';
import CompletarEntrevistaModal from '../modales/CompletarEntrevistaModal';
import { useManageGetRequest } from '../../../hooks/useManageGetRequest/useManageGetRequest';
import { getEntrevistasPorEvaluarAPI } from '../../../consumers/backendApisUrls';
import {
    TIPO_LABEL, formatearFecha,
    ContentWrapper, SectionTitle, SectionDescription, CardsList, ConvocatoriaCard,
    IconContainer, CardInformation, CardTitle, CardDetails, ProgressDetails,
    TypeBadge, ClosingDate, ApplyButton, EmptyState,
} from './shared';

// ──────────────────────────────────────────────────────────────────────────
// Pestaña "Entrevistas por completar" de "Mis Traslados y Promociones"
// (30/09/2026, a pedido de Jody). Postulantes que el empleado logueado tiene
// asignados como entrevistador (TP_EntrevistaDetalle.IdEmpleadoEntrevistador),
// pendientes de calificar. Sección aparte de "Convocatorias disponibles"
// porque la relación es distinta: aquí el empleado NO es el postulante.
//
// Ojo con el casing de los campos: GetEntrevistasPorEvaluar devuelve un
// ViewModel fuertemente tipado (EntrevistaPorEvaluarViewModel), así que el
// JSON viaja en PascalCase (IdEntrevistaDetalle, NombrePostulante, ...) — a
// diferencia de "Items" en ListarPublicadas (pestaña de convocatorias), que
// viaja como un JObject de paso y conserva el camelCase del Lambda Java.
// Confirmado comparando con EntrevistaPostulante.jsx (detalle?.NombreEmpleado),
// que consume el mismo tipo de ViewModel.
// ──────────────────────────────────────────────────────────────────────────

const EntrevistasPorEvaluar = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [entrevistas, setEntrevistas] = useState([]);
    const [modalEnabled, setModalEnabled] = useState({ isEnable: false });

    const [manageGetRequest] = useManageGetRequest();

    const successCallback = ({ data }) => {
        setEntrevistas(data ?? []);
    };

    const cargar = async () => {
        setIsLoading(true);
        try {
            await manageGetRequest(getEntrevistasPorEvaluarAPI(), successCallback, false);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCompletarEntrevista = (entrevista) => {
        setModalEnabled({ isEnable: true, component: CompletarEntrevistaModal, data: { entrevista } });
    };

    return (
        <PageWrapper isLoading={isLoading}>
            <ContentWrapper>
                <SectionTitle>Entrevistas por completar</SectionTitle>
                <SectionDescription>Postulantes que tienes asignados como entrevistador, pendientes de calificar</SectionDescription>

                <CardsList>
                    {entrevistas.map(entrevista => {
                        const tipo = TIPO_LABEL[entrevista.TipoProceso] ?? entrevista.TipoProceso;
                        return (
                            <ConvocatoriaCard key={entrevista.IdEntrevistaDetalle}>
                                <IconContainer tipo={tipo}>
                                    <FontAwesomeIcon icon="user-tie" />
                                </IconContainer>

                                <CardInformation>
                                    <CardTitle>{entrevista.NombrePostulante}</CardTitle>
                                    <CardDetails>
                                        <TypeBadge tipo={tipo}>{tipo}</TypeBadge>
                                        <ClosingDate>{entrevista.NombreConvocatoria}</ClosingDate>
                                    </CardDetails>
                                    <ProgressDetails>
                                        <span>Cargo: <strong>{entrevista.CargoPostulante}</strong></span>
                                        <span>Sede: <strong>{entrevista.SedePostulante}</strong></span>
                                        <span>Fecha: <strong>{formatearFecha(entrevista.FechaHora)}</strong></span>
                                    </ProgressDetails>
                                </CardInformation>

                                <ApplyButton type="button" onClick={() => handleCompletarEntrevista(entrevista)}>
                                    <FontAwesomeIcon icon="comment-dots" className="mr-1" />
                                    Completar entrevista
                                </ApplyButton>
                            </ConvocatoriaCard>
                        );
                    })}
                </CardsList>

                {!isLoading && entrevistas.length === 0 && (
                    <EmptyState>No tienes entrevistas pendientes por completar.</EmptyState>
                )}
            </ContentWrapper>

            <ModalEnabled modalEnabled={modalEnabled} setModalEnabled={setModalEnabled} refreshPage={cargar} />
        </PageWrapper>
    );
};

export default EntrevistasPorEvaluar;
